import React from 'react'
import styled, { css } from 'styled-components'
import { Tooltip, VisuallyHidden } from '@mote/ui'
import { isNumber } from '@mote/helpers'

export const Emoji = ({
  children,
  message,
  label = '',
  isEmojiPickerItem = false,
  isAnimated = true,
  isInteractive = false,
  noTooltip = false,
  emojiSize = 24,
  fontSize = 'inherit',
  onClick,
  ...rest
}) => {
  const isFocusable = React.useMemo(
    () => onClick || isInteractive,
    [onClick, isInteractive]
  )

  return (
    <EmojiContainer
      as={isFocusable ? 'button' : 'span'}
      type={isFocusable ? 'button' : null}
      role={!isFocusable ? 'img' : null}
      data-tip={message}
      aria-label={`${label} emoji`.trim()}
      isEmojiPickerItem={isEmojiPickerItem}
      isAnimated={isAnimated}
      onClick={onClick}
      isInteractive={isFocusable}
      tabIndex={!isFocusable ? -1 : null}
      emojiSize={emojiSize}
      fontSize={fontSize}
      {...rest}
    >
      {message && <VisuallyHidden>{message}</VisuallyHidden>}
      <StyledTooltip disabled={noTooltip} content={label} direction='up'>
        <span className='emoji' aria-hidden={true} tabIndex={-1}>
          {children}
        </span>
      </StyledTooltip>
    </EmojiContainer>
  )
}

const EmojiContainer = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 100%;
  outline: transparent solid 2px !important;
  outline-offset: 2px;
  transition: all 150ms ease-in;

  span.emoji {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  ${({ emojiSize }) => css`
    height: ${emojiSize}px;
    width: ${emojiSize}px;
  `};

  ${({ fontSize }) => css`
    span.emoji {
      font-size: ${isNumber(fontSize) ? `${fontSize}px` : fontSize};
      height: ${isNumber(fontSize) ? `${fontSize}px` : fontSize};
      width: ${isNumber(fontSize) ? `${fontSize}px` : fontSize};
    }
  `};

  ${({ isEmojiPickerItem }) =>
    isEmojiPickerItem &&
    css`
      padding: 0 10px;
    `};

  ${({ isInteractive }) =>
    isInteractive
      ? css`
          cursor: pointer;

          &:hover,
          &:focus {
            background: #f6f6f9;
          }
        `
      : css`
          cursor: default;
        `};

  ${({ isAnimated, isInteractive }) =>
    isInteractive &&
    isAnimated &&
    css`
      span.emoji {
        transition: transform 150ms ease-in;
      }

      &:hover,
      &:focus {
        span.emoji {
          transform: scale(1.2);
        }
      }
    `};
`

const StyledTooltip = styled(Tooltip)`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`
